/**
 * @file Broad phase + narrow phase collision resolution (GDD §12).
 *
 * Two passes over a rebuilt spatial hash:
 *  1. Bodies (player, enemies) are pushed out of walls and out of each other.
 *  2. Projectiles are swept along the segment they travelled this frame, so a
 *     62 u/s bullet cannot tunnel through a 2 u wall on a slow frame.
 *
 * The system never applies damage: it emits `bullet:hit` and `damage`, leaving
 * the rules to whoever listens.
 */

import * as THREE from 'three';
import { System } from '../ecs/System.js';
import {
  Transform,
  Velocity,
  Collider,
  Projectile,
  Health,
  PlayerControlled,
  Explosive
} from '../components/index.js';
import { DetonationCause } from '../components/Explosive.js';
import { SpatialHash } from '../physics/SpatialHash.js';
import { overlapAABB, createOverlapResult, raycastAABB, faceNormal } from '../physics/AABB.js';
import { boundsOf, createBounds, sweepBounds, canCollide } from '../physics/Collision.js';
import { claimDamage, falloffScale } from '../combat/Shot.js';
import { Events } from '../core/EventBus.js';
import { AIBrain } from '../components/AIBrain.js';

/** Push-out passes per frame; two keeps corners stable without jitter. */
const RESOLVE_ITERATIONS = 2;

export class CollisionSystem extends System {
  static priority = 50;

  constructor() {
    super();
    /** @type {SpatialHash | null} @private */
    this._hash = null;
    /** @type {Set<number>} Entities that have already triggered contact this frame. @private */
    this._contactedThisFrame = new Set();
    /** @type {any[]} Scratch array for broad-phase results. @private */
    this._candidates = [];
    /** @type {import('../physics/Collision.js').Bounds} @private */
    this._bounds = createBounds();
    /** @type {import('../physics/Collision.js').Bounds} @private */
    this._otherBounds = createBounds();
    /** @type {import('../physics/AABB.js').OverlapResult} @private */
    this._overlap = createOverlapResult();
    /** @type {{ x: number, z: number }} @private */
    this._normal = { x: 0, z: 0 };
    /** @type {THREE.Vector3} @private */
    this._hitPoint = new THREE.Vector3();
    /** @type {THREE.Vector3} @private */
    this._hitNormal = new THREE.Vector3();
    /** @type {{ x: number, z: number }} Reused travel direction of the round. @private */
    this._damageDirection = { x: 0, z: 0 };
    /** @type {object} @private */
    this._hitEvent = {
      position: this._hitPoint,
      normal: this._hitNormal,
      target: null,
      owner: null
    };
    /**
     * Projectile damage carries a fixed `'bullet'` type so destructible props
     * can accept or ignore gunfire per their `destroyedBy` list.
     * @type {object} @private
     */
    this._damageEvent = {
      target: null,
      amount: 0,
      source: null,
      point: this._hitPoint,
      type: 'bullet',
      /** Travel direction of the round, so a hit reaction can push along it. */
      direction: this._damageDirection,
      impulse: 0
    };
    /**
     * Every hit found along one projectile's sweep, ordered near to far. Slots
     * are reused rather than reallocated: this array is touched once per
     * projectile per frame.
     * @type {{ t: number, entity: import('../ecs/Entity.js').Entity | null }[]}
     * @private
     */
    this._hits = [];
    /** @type {number} Slots of `_hits` in use this sweep. @private */
    this._hitCount = 0;
  }

  /** @returns {void} */
  init() {
    this._hash = new SpatialHash(this.ctx.config.physics.cellSize);
  }

  /**
   * @param {number} _dt Delta time in seconds.
   * @returns {void}
   */
  update(_dt) {
    const bodies = this.world.query(Transform, Collider);
    if (bodies.length === 0) return;

    this._rebuildHash(bodies);
    this._resolveBodies(bodies);
    this._detectContacts();
    this._resolveProjectiles();
  }

  /**
   * @param {import('../ecs/Entity.js').Entity[]} bodies Every collidable entity.
   * @returns {void}
   * @private
   */
  _rebuildHash(bodies) {
    this._hash.clear();
    for (const entity of bodies) {
      const collider = entity.get(Collider);
      collider.touchedStatic = false;
      const bounds = boundsOf(entity.get(Transform), collider, this._bounds);
      this._hash.insert(entity, bounds.minX, bounds.minZ, bounds.maxX, bounds.maxZ);
    }
  }
  // --------------------------------------------------------- contact detection

  /**
   * Detects player-enemy body contact and emits `enemy:contact` so the AI
   * system can transition to aggro.  Uses the same spatial-hash overlap
   * checks already computed for physics — no extra broad-phase needed.
   *
   * @returns {void}
   * @private
   */
  _detectContacts() {
    this._contactedThisFrame.clear();

    // Find the live player.
    let playerEntity = null;
    let playerTransform = null;
    let playerCollider = null;
    for (const ent of this.world.query(PlayerControlled, Transform, Health)) {
      const h = ent.get(Health);
      if (h && !h.dead) {
        playerEntity = ent;
        playerTransform = ent.get(Transform);
        playerCollider = ent.get(Collider);
        break;
      }
    }
    if (!playerEntity || !playerTransform || !playerCollider) return;

    // Broad phase: only entities near the player (spatial hash already rebuilt).
    const pBounds = boundsOf(playerTransform, playerCollider, this._bounds);
    const candidates = this._hash.query(
      pBounds.minX, pBounds.minZ, pBounds.maxX, pBounds.maxZ,
      this._candidates
    );

    for (const enemy of candidates) {
      if (enemy === playerEntity || !enemy.alive) continue;
      // Skip projectiles.
      if (enemy.has(Projectile)) continue;
      // Skip if the enemy has no AIBrain — only AI enemies can go aggro.
      if (!enemy.has(AIBrain)) continue;
      const enemyCollider = enemy.get(Collider);
      if (!enemyCollider || !canCollide(playerCollider, enemyCollider)) continue;

      const enemyTransform = enemy.get(Transform);
      if (!enemyTransform) continue;
      const result = overlapAABB(
        playerTransform.position.x, playerTransform.position.z,
        playerCollider.halfX, playerCollider.halfZ,
        enemyTransform.position.x, enemyTransform.position.z,
        enemyCollider.halfX, enemyCollider.halfZ,
        this._overlap
      );
      if (!result.hit) continue;

      // Already reported this frame — skip duplicates (defensive; hash query
      // already dedupes, but keep the set if emit paths grow later).
      if (this._contactedThisFrame.has(enemy.id)) continue;
      this._contactedThisFrame.add(enemy.id);

      this.ctx.events.emit(Events.ENEMY_CONTACT, {
        enemy: enemy,
        player: playerEntity
      });
    }
  }

  // ------------------------------------------------------------------ bodies

  /**
   * @param {import('../ecs/Entity.js').Entity[]} bodies Every collidable entity.
   * @returns {void}
   * @private
   */
  _resolveBodies(bodies) {
    for (let pass = 0; pass < RESOLVE_ITERATIONS; pass++) {
      for (const entity of bodies) {
        const collider = entity.get(Collider);
        if (collider.isStatic || collider.isTrigger || entity.has(Projectile)) continue;
        this._resolveBody(entity, collider);
      }
    }
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Dynamic body.
   * @param {import('../components/Collider.js').Collider} collider Its collider.
   * @returns {void}
   * @private
   */
  _resolveBody(entity, collider) {
    const transform = entity.get(Transform);
    const bounds = boundsOf(transform, collider, this._bounds);
    const candidates = this._hash.query(
      bounds.minX,
      bounds.minZ,
      bounds.maxX,
      bounds.maxZ,
      this._candidates
    );

    for (const other of candidates) {
      if (other === entity || !other.alive) continue;
      const otherCollider = other.get(Collider);
      if (otherCollider.isTrigger || other.has(Projectile)) continue;
      if (!canCollide(collider, otherCollider)) continue;

      const otherTransform = other.get(Transform);
      const result = overlapAABB(
        transform.position.x,
        transform.position.z,
        collider.halfX,
        collider.halfZ,
        otherTransform.position.x,
        otherTransform.position.z,
        otherCollider.halfX,
        otherCollider.halfZ,
        this._overlap
      );
      if (!result.hit) continue;

      if (otherCollider.isStatic) {
        this._pushOutOfStatic(entity, transform, result);
      } else {
        this._separateDynamic(transform, otherTransform, result);
      }
    }
  }

  /**
   * Moves a body fully out of an immovable one and kills the velocity component
   * pointing into it, which is what produces wall sliding.
   *
   * @param {import('../ecs/Entity.js').Entity} entity Dynamic body.
   * @param {import('../components/Transform.js').Transform} transform Its transform.
   * @param {import('../physics/AABB.js').OverlapResult} result Overlap info.
   * @returns {void}
   * @private
   */
  _pushOutOfStatic(entity, transform, result) {
    const skin = this.ctx.config.physics.skin;
    transform.position.x += result.normalX * (result.depth + skin);
    transform.position.z += result.normalZ * (result.depth + skin);
    entity.get(Collider).touchedStatic = true;

    const velocity = entity.get(Velocity);
    if (!velocity) return;

    const into = velocity.linear.x * result.normalX + velocity.linear.z * result.normalZ;
    if (into < 0) {
      velocity.linear.x -= result.normalX * into;
      velocity.linear.z -= result.normalZ * into;
    }
  }

  /**
   * Splits the penetration between two movable bodies.
   *
   * @param {import('../components/Transform.js').Transform} a First transform.
   * @param {import('../components/Transform.js').Transform} b Second transform.
   * @param {import('../physics/AABB.js').OverlapResult} result Overlap info.
   * @returns {void}
   * @private
   */
  _separateDynamic(a, b, result) {
    const bias = this.ctx.config.physics.dynamicResolveBias;
    const shiftX = result.normalX * result.depth * bias;
    const shiftZ = result.normalZ * result.depth * bias;
    a.position.x += shiftX;
    a.position.z += shiftZ;
    b.position.x -= shiftX;
    b.position.z -= shiftZ;
  }

  // ------------------------------------------------------------- projectiles

  /**
   * Sweeps each projectile from its previous position to its current one and
   * resolves everything it crossed, nearest first.
   *
   * Resolving the *whole* segment rather than only its nearest hit is what
   * makes piercing honest: a rail round covers ~3.2 u per frame, more than the
   * width of two enemies, so keeping one hit per frame would let it pass
   * straight through the back of a packed row without ever touching it — and
   * the gap only widens as the frame rate drops.
   * @returns {void}
   * @private
   */
  _resolveProjectiles() {
    for (const entity of this.world.query(Projectile, Transform, Collider)) {
      if (!entity.alive) continue;

      const projectile = entity.get(Projectile);
      const transform = entity.get(Transform);
      const collider = entity.get(Collider);

      const from = transform.previousPosition;
      const to = transform.position;
      const dx = to.x - from.x;
      const dz = to.z - from.z;

      // Broad phase over the whole swept segment, not just the end position.
      this._bounds.minX = from.x - collider.halfX;
      this._bounds.maxX = from.x + collider.halfX;
      this._bounds.minZ = from.z - collider.halfZ;
      this._bounds.maxZ = from.z + collider.halfZ;
      sweepBounds(this._bounds, dx, dz);

      const candidates = this._hash.query(
        this._bounds.minX,
        this._bounds.minZ,
        this._bounds.maxX,
        this._bounds.maxZ,
        this._candidates
      );

      this._hitCount = 0;

      for (const other of candidates) {
        if (other === entity || !other.alive) continue;
        if (other.id === projectile.ownerId || other.has(Projectile)) continue;

        const otherCollider = other.get(Collider);
        if (!canCollide(collider, otherCollider)) continue;

        const otherTransform = other.get(Transform);
        // Minkowski expansion: inflate the target by the projectile's own size
        // so the sweep can be a zero-width ray.
        const t = raycastAABB(
          from.x,
          from.z,
          dx,
          dz,
          otherTransform.position.x - otherCollider.halfX - collider.halfX,
          otherTransform.position.z - otherCollider.halfZ - collider.halfZ,
          otherTransform.position.x + otherCollider.halfX + collider.halfX,
          otherTransform.position.z + otherCollider.halfZ + collider.halfZ
        );
        if (t < 0 || t > 1) continue;

        this._pushHit(t, other);
      }

      for (let i = 0; i < this._hitCount; i++) {
        const hit = this._hits[i];
        // An earlier hit in this same sweep may have killed it already.
        if (!hit.entity.alive) continue;
        const stopped = this._onProjectileHit(
          entity,
          projectile,
          transform,
          hit.entity,
          hit.t,
          dx,
          dz
        );
        if (stopped) break;
      }
    }
  }

  /**
   * Records one hit, keeping `_hits[0.._hitCount)` sorted by distance. Sweeps
   * cross a handful of colliders at most, so an insertion sort beats
   * `Array#sort` and — unlike it — allocates nothing.
   *
   * @param {number} t Hit parameter along the swept segment, 0..1.
   * @param {import('../ecs/Entity.js').Entity} target Entity that was crossed.
   * @returns {void}
   * @private
   */
  _pushHit(t, target) {
    let slot = this._hits[this._hitCount];
    if (!slot) {
      slot = { t: 0, entity: null };
      this._hits.push(slot);
    }

    let i = this._hitCount++;
    for (; i > 0 && this._hits[i - 1].t > t; i--) this._hits[i] = this._hits[i - 1];
    this._hits[i] = slot;
    slot.t = t;
    slot.entity = target;
  }

  /**
   * @param {import('../ecs/Entity.js').Entity} entity Projectile entity.
   * @param {import('../components/Projectile.js').Projectile} projectile Projectile data.
   * @param {import('../components/Transform.js').Transform} transform Projectile transform.
   * @param {import('../ecs/Entity.js').Entity} target Entity that was hit.
   * @param {number} t Hit parameter along the swept segment.
   * @param {number} dx Segment length along X.
   * @param {number} dz Segment length along Z.
   * @returns {boolean} True if the projectile was stopped by this hit.
   * @private
   */
  _onProjectileHit(entity, projectile, transform, target, t, dx, dz) {
    // Already damaged this body on an earlier frame of the same flight: keep
    // flying without re-applying damage or burning another pierce charge.
    if (target.has(Health) && projectile.hitIds.includes(target.id)) return false;

    const from = transform.previousPosition;
    this._hitPoint.set(from.x + dx * t, transform.position.y, from.z + dz * t);

    const targetCollider = target.get(Collider);
    const targetTransform = target.get(Transform);
    faceNormal(
      this._hitPoint.x,
      this._hitPoint.z,
      targetTransform.position.x,
      targetTransform.position.z,
      targetCollider.halfX + 1e-4,
      targetCollider.halfZ + 1e-4,
      this._normal
    );
    this._hitNormal.set(this._normal.x, 0, this._normal.z);

    if (target.has(Health)) {
      // A piercing round books every body it passes through, so it can never
      // re-hit one it is still overlapping on the next sweep.
      projectile.hitIds.push(target.id);

      // Distance is measured to the impact itself, not to wherever the frame
      // ended, so a pellet that just reaches its falloff edge is judged there.
      const travelled = projectile.travelled + Math.hypot(dx, dz) * t;
      const amount = claimDamage(
        projectile.shot,
        target.id,
        projectile.damage *
          falloffScale(travelled, projectile.range, projectile.falloffStart, projectile.falloffMin)
      );

      // A shot that has already saturated this target still sparks and dies —
      // it just adds no damage.
      if (amount > 0) {
        this._damageEvent.target = target;
        this._damageEvent.amount = amount;
        this._damageEvent.source = this.world.getEntity(projectile.ownerId) ?? null;
        // The swept segment *is* the travel direction, so the shove follows the
        // round's own path rather than a line back to whoever fired it.
        this._damageDirection.x = dx;
        this._damageDirection.z = dz;
        this._damageEvent.impulse = this.ctx.config.knockback.bulletImpulse * projectile.knockback;
        this.ctx.events.emit(Events.DAMAGE, this._damageEvent);
      }
    }

    this._hitEvent.target = target;
    this._hitEvent.owner = this.world.getEntity(projectile.ownerId) ?? null;
    this.ctx.events.emit(Events.BULLET_HIT, this._hitEvent);

    // Pierce only ever spends itself on things that can be hurt: a wall stops
    // the round however much pierce is left.
    if (projectile.pierce > 0 && target.has(Health)) {
      projectile.pierce--;
      return false;
    }

    // An explosive round goes off where the sweep says it stopped, not where the
    // frame left it — by then it is already a little way inside the wall.
    const explosive = entity.get(Explosive);
    if (explosive) {
      explosive.hasImpact = true;
      explosive.impactX = this._hitPoint.x;
      explosive.impactZ = this._hitPoint.z;
      explosive.cause = target.has(Health) ? DetonationCause.ENEMY : DetonationCause.WALL;
    }

    this.world.destroyEntity(entity);
    return true;
  }
}
